import { ApolloError, gql } from '@apollo/client';

import { describeError, getRateLimitRejection } from '../../../common/rateLimit';

/**
 * Adding a person to the CRM by hand (`crmCreateContact`). The person is a
 * CRM contact only: no app account is made or linked. Consent is given after,
 * as a one-person batch update (CRM_SET_CONTACT_FLAG), which needs a note
 * saying how they agreed, as the contact page's consent buttons do.
 */

export const CRM_CREATE_CONTACT = gql`
  mutation CrmCreateContact($input: CrmNewContactInput!, $allowDuplicate: Boolean) {
    crmCreateContact(input: $input, allowDuplicate: $allowDuplicate) {
      id
      fullName
    }
  }
`;

export type PhoneType = 'MOBILE' | 'HOME' | 'WORK';

export interface NewPerson {
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  phoneType: PhoneType;
  address1: string;
  address2: string;
  city: string;
  region: string;
  zip: string;
  countryCode: string;
  supportLevel: number | null;
  stage: string;
  pointPerson: number | null;
  tagIds: Array<number>;
  note: string;
  emailOptIn: boolean;
  smsOptIn: boolean;
  /** How they agreed: required when either opt-in is ticked. */
  consentNote: string;
}

export const EMPTY_PERSON: NewPerson = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  phoneType: 'MOBILE',
  address1: '',
  address2: '',
  city: '',
  region: '',
  zip: '',
  countryCode: '',
  supportLevel: null,
  stage: '',
  pointPerson: null,
  tagIds: [],
  note: '',
  emailOptIn: false,
  smsOptIn: false,
  consentNote: '',
};

const EMAIL = /^[^\s@]+@[^\s@.]+(\.[^\s@.]+)+$/;
const PHONE = /^[+\d\s().\-/]+$/;

/** What stops the form being sent, by field. The API checks again; these are the ones worth saying at once. */
export const personProblems = (p: NewPerson): Partial<Record<keyof NewPerson, string>> => {
  const out: Partial<Record<keyof NewPerson, string>> = {};
  if (!p.firstName.trim() && !p.lastName.trim()) out.firstName = 'Give a first or a last name.';
  if (p.email.trim() && !EMAIL.test(p.email.trim())) out.email = 'Not an email address.';
  if (p.phone.trim()) {
    if (!PHONE.test(p.phone.trim())) out.phone = 'Digits, with + for the country code.';
    else if (!p.phone.trim().startsWith('+') && !p.phone.trim().startsWith('00') && !p.countryCode) {
      out.phone = 'Start with + and the country code, or choose their country below.';
    }
  }
  if ((p.address2.trim() || p.zip.trim()) && !p.address1.trim()) out.address1 = 'Give the first line of the address.';
  if (p.emailOptIn && !p.email.trim()) out.emailOptIn = 'Add their email address to opt them in to emails.';
  if (p.smsOptIn && !p.phone.trim()) out.smsOptIn = 'Add their number to opt them in to texts.';
  if ((p.emailOptIn || p.smsOptIn) && !p.consentNote.trim()) out.consentNote = 'Say how they agreed: it is kept with their consent.';
  return out;
};

const text = (value: string) => (value.trim() ? value.trim() : undefined);

/** The form as `CrmNewContactInput`: only what was filled in. */
export const createInput = (p: NewPerson) => {
  const address =
    // An address is its first line; town, region and country complete it, or are only their place.
    p.address1.trim()
      ? {
          address1: text(p.address1),
          address2: text(p.address2),
          city: text(p.city),
          state: text(p.region),
          zip: text(p.zip),
          countryCode: p.countryCode || undefined,
        }
      : undefined;
  const input: Record<string, unknown> = {
    firstName: text(p.firstName),
    lastName: text(p.lastName),
    email: text(p.email),
    phone: text(p.phone),
    phoneType: p.phone.trim() ? p.phoneType : undefined,
    address,
    // With an address the API takes the place from it; without one, from these.
    countryCode: address ? undefined : p.countryCode || undefined,
    region: address ? undefined : text(p.region),
    city: address ? undefined : text(p.city),
    supportLevel: p.supportLevel ?? undefined,
    stage: text(p.stage),
    pointPerson: p.pointPerson ?? undefined,
    note: text(p.note),
    tagIds: p.tagIds.length ? p.tagIds : undefined,
  };
  return Object.fromEntries(Object.entries(input).filter(([, v]) => v !== undefined));
};

/** The consent to give once they are in, as a one-person batch update, or null for none. */
export const consentInput = (p: NewPerson) => {
  if (!p.emailOptIn && !p.smsOptIn) return null;
  return {
    fields: { ...(p.emailOptIn ? { emailOptIn: true } : {}), ...(p.smsOptIn ? { smsOptIn: true } : {}) },
    note: p.consentNote.trim(),
  };
};

/* ------------------------------------------------------------------ *
 * Refusals
 * ------------------------------------------------------------------ */

export interface ExistingMatch {
  id: number;
  name: string | null;
  on: 'EMAIL' | 'PHONE';
}

const firstError = (error: unknown) => (error instanceof ApolloError ? error.graphQLErrors?.[0] : undefined);

/** The contacts already holding this email or number, when the add was refused for them. */
export const existingMatches = (error: unknown): Array<ExistingMatch> | null => {
  const first = firstError(error);
  if (first?.extensions?.code !== 'CRM_CONTACT_EXISTS') return null;
  const matches = first.extensions.matches;
  return Array.isArray(matches) ? (matches as Array<ExistingMatch>) : [];
};

/** The form field a refusal names: `input.email` → email. */
export const fieldOfRefusal = (error: unknown): keyof NewPerson | undefined => {
  const field = firstError(error)?.extensions?.field;
  if (typeof field !== 'string') return undefined;
  const key = field.replace(/^input\./, '').replace(/^address\.(address1|address2|city|zip|countryCode)$/, '$1').replace(/^address\.state$/, 'region');
  const map: Record<string, keyof NewPerson> = { address: 'address1', stage: 'stage', pointPerson: 'pointPerson', tagIds: 'tagIds', phoneType: 'phone' };
  return (map[key] ?? (key in EMPTY_PERSON ? key : undefined)) as keyof NewPerson | undefined;
};

export const describeAddError = (error: unknown): string => {
  if (getRateLimitRejection(error)) return describeError(error, 'Too many changes at once');
  const first = firstError(error);
  if (first?.extensions?.code === 'GRAPHQL_VALIDATION_FAILED' || /Cannot query field|Unknown type/.test(first?.message ?? '')) {
    return 'The API this dashboard uses cannot add people yet. Deploy the API first.';
  }
  if (first?.message) return first.message;
  if (error instanceof ApolloError && error.networkError) return 'Could not reach the API. Check the contact list before trying again.';
  return 'The person could not be added.';
};
